<template>
  <div :id="id" class="c-property-lookup">
    <div class="c-property-lookup__group">
      <div class="c-property-lookup__lookup">
        <label v-if="label" class="c-property-lookup__label"
          >{{ label }}
        </label>

        <form
          v-if="!lookupDisabled"
          class="c-property-lookup__form"
          @submit.prevent="onPropertyLookup"
        >
          <div class="c-form__group">
            <validation-message
              :valid="(touched['search'] && searchIsValid)"
              :invalid="(touched['search'] && !searchIsValid)"
              :validationMessage="getValidationMessage"
            >
              <text-field
                type="search"
                :key="getFieldId"
                :label="lookupLabel"
                :id="getFieldId"
                :value="field"
                @input="onSearchInput"
                @submit="onPropertyLookup"
                @change="onSearchChange"
              />
            </validation-message>
          </div>
          <button
            class="c-property-lookup__button"
            :class="{ 'is-loading': loading }"
            type="submit"
            :disabled="loading"
          >
            {{ searchButton }}
            <div v-if="loading" class="c-spinner">
              <div></div>
              <div></div>
              <div></div>
            </div>
          </button>
        </form>

        <collapsible-container>
          <collapsible-container-content
            title="Select address"
            index="0"
            :isOpen="selectAddressContainerIsOpen"
          >
            <validation-message
              :valid="locationIsValid"
              :invalid="address.value.length > 0 && !locationIsValid"
              :validationMessage="getLocationInvalidMessage"
            >
              <select-dropdown
                id="selectAddress"
                label="Select address"
                :options="availableAddresses"
                v-model="address.value"
                @change="onSelectAddressChange"
              >
              </select-dropdown>
            </validation-message>
          </collapsible-container-content>
        </collapsible-container>
      </div>
    </div>
  </div>
</template>

<script>
  import endpoints from "Globals/mando.endpoints";
  import axios from "axios";
  import TextField from "../forms/input/TextField.vue";
  import SelectDropdown from "../forms/select/SelectDropdown.vue";
  import ValidationMessage from "../../../base/validation-message/ValidationMessage.vue";
  import CollapsibleContainer from "../collapsible-container/CollapsibleContainer.vue";
  import CollapsibleContainerContent from "../collapsible-container/CollapsibleContainerContent.vue";

  export default {
    props: {
      id: {
        required: true,
        type: [String, Number],
      },
      label: {
        type: String,
        required: true,
      },
      searchButton: {
        type: String,
        required: false,
        default: "Find address",
      },
      lookupFormatError: {
        type: String,
        required: true,
      },
      lookupLabel: {
        type: String,
        required: true,
      },
      lookupValue: {
        type: String,
        default: "",
      },
      lookupDisabled: {
        type: Boolean,
      },
      isCustomerReference: {
        type: Boolean,
        default: false,
      },
      notListedError: {
        type: String,
        required: true,
      },
      notAllowedError: {
        type: String,
        required: true,
      },
      networkError: {
        type: String,
        default: "Network error, please try again",
      },
      customersOnly: {
        type: Boolean,
        default: false,
      },
    },
    components: {
      ValidationMessage,
      TextField,
      SelectDropdown,
      CollapsibleContainer,
      CollapsibleContainerContent,
    },
    data() {
      return {
        selectAddressContainerIsOpen: false,
        enterAddressContainerIsOpen: false,
        loading: false,
        searchIsValid: false,
        addressNotlisted: false,
        field: this.lookupValue,
        availableAddresses: [],
        networkRequestError: false,
        address: {
          value: "",
          label: "",
          wessexProvider: false,
        },
        touched: {
          search: false,
        },
      };
    },
    computed: {
      getAddressFieldValidationMsg() {
        return (field) => {
          return field.valid ? null : field.validationMessage;
        };
      },
      getValidationMessage() {
        if (!this.touched["search"]) return null;
        if (this.networkRequestError) return this.networkError;
        if (!this.searchIsValid && !this.addressNotlisted)
          return this.lookupFormatError;
        if (this.addressNotlisted) return this.notListedError;
      },
      getFieldId() {
        return `${this.id}-field`;
      },
      getLocationInvalidMessage() {
        if (this.customersOnly && this.address.value.length > 0) {
          return !this.locationIsValid ? this.notAllowedError : null;
        }
        return null;
      },
      locationIsValid() {
        return this.customersOnly
          ? this.address.wessexProvider
          : this.address.value.length > 0;
      },
    },
    methods: {
      onSearchChange() {
        this.touched["search"] = true;
      },
      onSearchInput(value) {
        this.field = value;
        if (this.addressNotlisted) {
          this.addressNotlisted = false;
        }

        if (this.networkRequestError) {
          this.networkRequestError = false;
        }

        if (this.isCustomerReference) {
          this.validateCustomerReference();
        } else {
          this.validatePostcode();
        }
      },
      validateCustomerReference() {
        if (!this.isValidCustomerReference(this.field)) {
          this.searchIsValid = false;
        } else {
          this.searchIsValid = true;
        }
      },
      validatePostcode() {
        const formattedPostcode = this.reFormatPostcodeValue(this.field);
        this.field = formattedPostcode;

        if (!this.isValidPostcode(formattedPostcode)) {
          this.searchIsValid = false;
        } else {
          this.searchIsValid = true;
        }
      },
      onPropertyLookup() {
        this.touched["search"] = true;
        if (!this.loading && this.searchIsValid) {
          this.$emit("onLookUpValue", this.field);
          this.requestData();
          this.$emit("onSubmit", this.field);
        }
      },
      onSelectAddressChange(address) {
        this.address = address;
        this.field = "";
        this.searchIsValid = false;
        this.touched["search"] = false;
        this.$emit("locationValid", this.locationIsValid);
        this.$emit("selectedAddress", this.address);
      },
      reFormatPostcodeValue(value) {
        const formattedValue = value.replace(/[^A-Za-z0-9]/g, "").toUpperCase();
        const spacedPostcode = formattedValue.replace(
          /^([A-Za-z]{1,2}\d{1,2})(\d{1})([A-Za-z]{2})$/,
          "$1 $2$3"
        );
        return spacedPostcode;
      },
      isValidCustomerReference(number) {
        const customerRefNumRegex = /^\d{8}$/;
        return customerRefNumRegex.test(number);
      },
      isValidPostcode(value) {
        const postcodeRegex = /^[A-Z]{1,2}[0-9][A-Z0-9]? ?[0-9][A-Z]{2}$/;
        return postcodeRegex.test(value);
      },
      resetLoad() {
        this.addressNotlisted = false;
        this.selectAddressContainerIsOpen = false;
        this.address = {
          value: "",
          label: "",
          wessexProvider: false,
        };
        this.$emit("locationValid", this.locationIsValid);
        this.$emit("selectedAddress", this.address);
      },
      requestData() {
        const formatedValue = this.field.toLowerCase().split(" ").join("+");

        if (!formatedValue) {
          return null;
        }
        this.resetLoad();
        this.loading = true;

        const queryStrings = this.isCustomerReference
          ? `customerReference=${formatedValue}`
          : `postcode=${formatedValue}`;

        axios
          .get(`${endpoints("getContactDetails")}?${queryStrings}`)
          .then((response) => {
            this.loading = false;
            this.addressNotlisted = response.data.error;

            if (this.addressNotlisted) {
              this.searchIsValid = false;
            }

            const results = response.data.results;

            if (results && results.length) {
              const formatAddress = (address) => {
                const addressArray = address.split(",");
                const formattedAddress = addressArray
                  .map((line, i) => {
                    if (i !== addressArray.length - 1) {
                      return line
                        .split(" ")
                        .map(
                          (word) =>
                            word.charAt(0).toUpperCase() +
                            word.slice(1).toLowerCase()
                        )
                        .join(" ");
                    }
                    return line.toUpperCase();
                  })
                  .join(",");
                return formattedAddress;
              };
              const formattedResults = results.map((result) => ({
                label: formatAddress(result.label),
                value: result.value,
                wessexProvider: result.wessexProvider,
              }));
              this.availableAddresses = formattedResults;

              this.selectAddressContainerIsOpen = true;
            }
          })
          .catch((err) => {
            this.loading = false;
            this.networkRequestError = true;
            this.searchIsValid = false;
          });
      },
    },
  };
</script>



// WEBPACK FOOTER //
// src/components/components/consumer/property-lookup/PropertyLookup.vue